import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWAInstall } from '../contexts/PWAInstallContext';
import { Download, X, Smartphone, ChevronRight } from 'lucide-react';

/**
 * PWA Install Banner (first-visit nudge).
 *
 * The actual install flow + help modal live in PWAInstallContext now — this
 * component is just the slide-up CTA on the FrontPage that triggers the
 * shared `openInstallFlow('banner')` action. Dismissal still uses the
 * 7-day localStorage cooldown.
 */
const PWAInstallBanner = () => {
  const { t } = useTranslation();
  const {
    showBanner, platform, installed, canNativeInstall,
    openInstallFlow, dismissBanner, markInstallSurfaceVisible,
  } = usePWAInstall();

  useEffect(() => {
    if (showBanner && !installed) markInstallSurfaceVisible('banner');
  }, [showBanner, installed, markInstallSurfaceVisible]);

  if (!showBanner || installed) return null;

  const variantHint = platform.isInAppBrowser
    ? t('pwaInstallInAppHint', 'Tap here — needs to be opened in Chrome/Safari first')
    : platform.isIOS || platform.isAndroid
      ? t('pwaInstallTapHint', 'Tap here to see how')
      : t('pwaInstallDesktopHint', 'Open this page on your phone to install.');

  return (
    <div
      className="fixed bottom-16 left-0 right-0 z-[60] px-4 pb-4 pt-2 animate-slide-up"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Smartphone className="h-5 w-5 text-white shrink-0" />
            <span className="text-white font-semibold text-sm truncate">{t('pwaInstallTitle')}</span>
          </div>
          <button
            onClick={dismissBanner}
            className="text-white/80 hover:text-white p-2 -mr-1"
            data-testid="pwa-dismiss-btn"
            aria-label={t('pwaDismissAria', 'Dismiss install banner')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {canNativeInstall ? (
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
              <Download className="h-5 w-5 text-purple-600" />
            </div>
            <p className="flex-1 text-xs text-gray-600 leading-snug">{t('pwaInstallDesc')}</p>
            <button
              onClick={() => openInstallFlow('banner')}
              className="px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 active:bg-purple-800 transition-colors shrink-0"
              data-testid="pwa-install-btn"
            >
              {t('pwaInstallBtn')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => openInstallFlow('banner')}
            className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
            data-testid="pwa-show-instructions-btn"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
              <Download className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{t('pwaInstallDesc')}</p>
              <p className="text-xs text-gray-500 mt-0.5">{variantHint}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PWAInstallBanner;
